import { useCallback, useEffect, useRef, useState } from "react";

import { useSession } from "@/features/organization";

import { fetchAuditLogs } from "./audit-log-api";
import type { AuditEventCursor, AuditEventEntry } from "./audit-log-types";

export interface AuditLogFilters {
  action: string;
}

export const PAGE_SIZE = 50;

export interface UseAuditLogReturn {
  entries: readonly AuditEventEntry[];
  isLoading: boolean;
  error: string | null;
  filters: AuditLogFilters;
  setFilters: (filters: AuditLogFilters) => void;
  hasMore: boolean;
  hasPrevious: boolean;
  pageNumber: number;
  loadNext: () => void;
  loadPrevious: () => void;
  reload: () => void;
  selectedEntry: AuditEventEntry | null;
  selectEntry: (entry: AuditEventEntry | null) => void;
}

export function useAuditLog(): UseAuditLogReturn {
  const { session } = useSession();
  const enterpriseId = session?.activeOrgUnit?.enterprise_id ?? null;

  const [entries, setEntries] = useState<readonly AuditEventEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<AuditEventEntry | null>(null);
  const [filters, setFiltersState] = useState<AuditLogFilters>({ action: "" });
  const [cursorStack, setCursorStack] = useState<AuditEventCursor[]>([]);
  const [currentCursor, setCurrentCursor] = useState<AuditEventCursor | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<AuditEventCursor | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  const requestIdRef = useRef(0);

  const loadLogs = useCallback(
    async (currentFilters: AuditLogFilters, cursor: AuditEventCursor | null) => {
      if (!enterpriseId) {
        ++requestIdRef.current;
        setEntries([]);
        setHasMore(false);
        setNextCursor(null);
        setError(null);
        setSelectedEntry(null);
        setIsLoading(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      setError(null);
      setSelectedEntry(null);

      try {
        const result = await fetchAuditLogs({
          enterprise_id: enterpriseId,
          ...(currentFilters.action ? { action: currentFilters.action } : {}),
          limit: PAGE_SIZE,
          ...(cursor ? { cursor_created_at: cursor.created_at, cursor_id: cursor.id } : {}),
        });
        if (requestIdRef.current !== requestId) return;
        setEntries(result.entries);
        setHasMore(result.hasMore);
        setNextCursor(result.nextCursor);
      } catch (e) {
        if (requestIdRef.current !== requestId) return;
        setError(e instanceof Error ? e.message : "Failed to load audit events");
        setEntries([]);
        setHasMore(false);
        setNextCursor(null);
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [enterpriseId]
  );

  useEffect(() => {
    void loadLogs(filters, currentCursor);
  }, [filters, currentCursor, reloadCounter, loadLogs]);

  return {
    entries,
    isLoading,
    error,
    filters,
    setFilters: (newFilters: AuditLogFilters) => {
      setCursorStack([]);
      setCurrentCursor(null);
      setFiltersState(newFilters);
    },
    hasMore,
    hasPrevious: cursorStack.length > 0,
    pageNumber: cursorStack.length + 1,
    loadNext: () => {
      if (!nextCursor) return;
      setCursorStack((stack) => [...stack, nextCursor]);
      setCurrentCursor(nextCursor);
    },
    loadPrevious: () => {
      setCursorStack((stack) => {
        const newStack = stack.slice(0, -1);
        const prevCursor = newStack[newStack.length - 1] ?? null;
        setCurrentCursor(prevCursor);
        return newStack;
      });
    },
    reload: () => setReloadCounter((c) => c + 1),
    selectedEntry,
    selectEntry: setSelectedEntry,
  };
}
